import type { StateCreator } from 'zustand';
import type AppEventsSlice from '../../interfaces/AppEventsSlice';
import type {
  FileCreationEventsActions,
  FileCreationEventsState,
  FileTemplateType,
  KeyboardShortcutsEventsActions,
  KeyboardShortcutsEventsState,
} from '../../interfaces/AppEventsSlice';
import type { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import type {
  GetAllFileExtensionsResponse,
  GetFileExtensionsByCategoryResponse,
} from '../../interfaces/FileExtensionApiService';
import type { RemotegrantApplication } from '../../interfaces/SettingApiService';
import type Tag from '../../interfaces/Tag';

/**
 * Zustand slice creator for application-wide events.
 * This slice manages session expiration and SSO provisioning state and actions.
 * All logic is encapsulated for robust, enterprise-grade state management.
 */
export const createAppEventsSlice: StateCreator<AppEventsSlice, [], [], AppEventsSlice> = (
  set,
  get,
) => ({
  appEvents: {
    appUpdated: {
      isOpen: false,
      open: () =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            appUpdated: { ...s.appEvents.appUpdated, isOpen: true },
          },
        })),
    },
    activateTotp: {
      isOpen: false,
      remindAt: null,
      neverShowAgain: false,
      isMandatory: false,
      remindDays: 7,
      open: (options) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            activateTotp: {
              ...s.appEvents.activateTotp,
              isMandatory: options?.isMandatory ?? false,
              isOpen: true,
              remindDays: options?.remindDays ?? 7,
            },
          },
        })),
      close: () =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            activateTotp: {
              ...s.appEvents.activateTotp,
              isMandatory: false,
              isOpen: false,
              remindDays: 7,
            },
          },
        })),
      remindForDays: (days = 7) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            activateTotp: {
              ...s.appEvents.activateTotp,
              isOpen: false,
              remindAt: Date.now() + days * 24 * 60 * 60 * 1000,
            },
          },
        })),
      setNeverShowAgain: (value: boolean) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            activateTotp: { ...s.appEvents.activateTotp, neverShowAgain: value },
          },
        })),
    },
    ssoLogin: {
      isSsoLogin: false,
      setIsSsoLogin: (value: boolean) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            ssoLogin: { ...s.appEvents.ssoLogin, isSsoLogin: value },
          },
        })),
      reset: () =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            ssoLogin: { ...s.appEvents.ssoLogin, isSsoLogin: false },
          },
        })),
    },
    sessionExpired: {
      // --- STATE ---
      /** Indicates if the session expired modal is currently open */
      isOpen: false,
      /** Reason for session expiration, if any */
      reason: undefined,
      /** Countdown in seconds before automatic logout */
      secondsLeft: 5,
      /** Internal lock to prevent multiple modal opens */
      _lock: false,
      /** Timer ID for scheduled logout (setTimeout) */
      _timerId: null,
      /** Timer ID for countdown tick (setInterval) */
      _tickId: null,

      // --- ACTIONS ---

      /**
       * Opens the session expired modal if not already open.
       * Sets the reason and countdown seconds, and locks further opens until closed.
       * @param reason - The reason for session expiration
       * @param seconds - Countdown duration in seconds (default: 5)
       */
      open: (reason, seconds = 5) =>
        set((s) => {
          const cur = s.appEvents.sessionExpired;
          // Prevent opening if already locked (modal is open)
          if (cur._lock) return s;
          return {
            appEvents: {
              ...s.appEvents,
              sessionExpired: {
                ...cur,
                isOpen: true,
                reason,
                secondsLeft: seconds,
                _lock: true,
              },
            },
          };
        }),

      /**
       * Closes the session expired modal and resets all related state and timers.
       * Ensures all timeouts and intervals are cleared to prevent memory leaks.
       */
      close: () =>
        set((s) => {
          const cur = s.appEvents.sessionExpired;
          if (cur._timerId) clearTimeout(cur._timerId);
          if (cur._tickId) clearInterval(cur._tickId);
          return {
            appEvents: {
              ...s.appEvents,
              sessionExpired: {
                ...cur,
                isOpen: false,
                reason: undefined,
                secondsLeft: 5,
                _lock: false,
                _timerId: null,
                _tickId: null,
              },
            },
          };
        }),

      /**
       * Decrements the countdown timer by one second, never going below zero.
       * Used for updating the UI countdown for session expiration.
       */
      tick: () =>
        set((s) => {
          const cur = s.appEvents.sessionExpired;
          return {
            appEvents: {
              ...s.appEvents,
              sessionExpired: {
                ...cur,
                secondsLeft: Math.max(0, cur.secondsLeft - 1),
              },
            },
          };
        }),

      /**
       * Schedules the automatic logout and countdown tick.
       * Cleans up any previous timers before starting new ones to ensure only one set is active.
       * @param ms - Milliseconds until logout (default: 5000)
       * @param onLogout - Optional callback to execute on logout
       */
      scheduleLogout: (ms = 5000, onLogout) =>
        set((s) => {
          const cur = s.appEvents.sessionExpired;
          // Clear any existing timers to avoid duplicate intervals/timeouts
          if (cur._timerId) clearTimeout(cur._timerId);
          if (cur._tickId) clearInterval(cur._tickId);

          // Start countdown interval for updating secondsLeft
          const tickId = window.setInterval(() => {
            get().appEvents.sessionExpired.tick();
          }, 1000);

          // Schedule logout after the specified timeout
          const timerId = window.setTimeout(() => {
            onLogout?.();
            get().appEvents.sessionExpired.close();
          }, ms);

          return {
            appEvents: {
              ...s.appEvents,
              sessionExpired: {
                ...cur,
                _timerId: timerId,
                _tickId: tickId,
              },
            },
          };
        }),

      /**
       * Clears any active timers for session expiration.
       * This is useful for cleanup when the modal is closed or the session is restored.
       */
      clearTimers: () =>
        set((s) => {
          const cur = s.appEvents.sessionExpired;
          if (cur._timerId) clearTimeout(cur._timerId);
          if (cur._tickId) clearInterval(cur._tickId);
          return {
            appEvents: {
              ...s.appEvents,
              sessionExpired: {
                ...cur,
                _timerId: null,
                _tickId: null,
              },
            },
          };
        }),
    },
    ssoProvisioning: {
      // --- STATE ---
      /** Epoch timestamp (ms) when SSO provisioning was requested; null if never requested */
      requestedAt: null,
      /** Maximum allowed time (ms) for SSO provisioning before timeout */
      timeoutMs: 3 * 60 * 1000, // 3 minutes in milliseconds
      /** Polling interval (ms) for checking SSO provisioning status */
      pollIntervalMs: 5000, // 5 seconds in milliseconds
      /** Polling interval ID (setInterval) for SSO provisioning */
      _pollId: null,

      // --- ACTIONS ---

      /**
       * Marks the SSO provisioning request as started at the current time.
       * Updates the requestedAt timestamp to the current epoch time.
       */
      markRequestedNow: () =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            ssoProvisioning: {
              ...s.appEvents.ssoProvisioning,
              requestedAt: Date.now(),
            },
          },
        })),

      /**
       * Clears the SSO provisioning state and stops any active polling.
       * Resets requestedAt and polling interval ID.
       */
      clear: () =>
        set((s) => {
          const cur = s.appEvents.ssoProvisioning;
          if (cur._pollId) clearInterval(cur._pollId);
          return {
            appEvents: {
              ...s.appEvents,
              ssoProvisioning: {
                ...cur,
                requestedAt: null,
                _pollId: null,
              },
            },
          };
        }),

      /**
       * Determines if there is an active, non-expired SSO provisioning request.
       * Returns true if the request is still within the allowed timeout window.
       */
      isActive: () => {
        const { requestedAt, timeoutMs } = get().appEvents.ssoProvisioning;
        return !!requestedAt && Date.now() - requestedAt < timeoutMs;
      },

      /**
       * Starts polling for SSO provisioning status.
       * Polls using the provided check function at the configured interval until either:
       *   - The check resolves to true (provisioning enabled), or
       *   - The timeout is reached (provisioning expired).
       * Handles cleanup of previous pollers and ensures only one poller is active.
       * @param check - Async function to check SSO provisioning status
       * @param onEnabled - Optional callback when provisioning is enabled
       * @param onTimeout - Optional callback when provisioning times out
       */
      startPolling: (
        check: () => Promise<boolean>,
        onEnabled?: () => void,
        onTimeout?: () => void,
      ) =>
        set((s) => {
          const cur = s.appEvents.ssoProvisioning;

          // Stop any previous polling interval to avoid duplicates
          if (cur._pollId) clearInterval(cur._pollId);

          // Use existing requestedAt or set to now if not present
          const requestedAt = cur.requestedAt ?? Date.now();

          /**
           * Polling tick function.
           * Checks for timeout and invokes the check function.
           * Handles cleanup and callback invocation on success or timeout.
           */
          const doTick = async () => {
            const now = Date.now();
            const snap = get().appEvents.ssoProvisioning;

            // If timeout has been reached, stop polling and invoke timeout callback
            if (now - requestedAt >= snap.timeoutMs) {
              if (snap._pollId) clearInterval(snap._pollId);
              // Only reset the poller; keep requestedAt for consumers
              set((s2) => ({
                appEvents: {
                  ...s2.appEvents,
                  ssoProvisioning: { ...s2.appEvents.ssoProvisioning, _pollId: null },
                },
              }));
              onTimeout?.();
              return;
            }

            try {
              // Attempt to check SSO provisioning status
              const ok = await check();
              if (ok) {
                const snap2 = get().appEvents.ssoProvisioning;
                if (snap2._pollId) clearInterval(snap2._pollId);
                set((s2) => ({
                  appEvents: {
                    ...s2.appEvents,
                    ssoProvisioning: { ...s2.appEvents.ssoProvisioning, _pollId: null },
                  },
                }));
                onEnabled?.();
              }
            } catch {
              // Ignore transient errors; continue polling until timeout
            }
          };

          // Start polling interval for SSO provisioning status
          const pollId = window.setInterval(doTick, cur.pollIntervalMs);
          // Execute the first polling tick immediately
          void doTick();

          return {
            appEvents: {
              ...s.appEvents,
              ssoProvisioning: {
                ...cur,
                requestedAt,
                _pollId: pollId,
              },
            },
          };
        }),

      /**
       * Stops the SSO provisioning polling interval.
       * Does not reset timestamps, only clears the polling interval.
       */
      stopPolling: () =>
        set((s) => {
          const cur = s.appEvents.ssoProvisioning;
          if (cur._pollId) clearInterval(cur._pollId);
          return {
            appEvents: {
              ...s.appEvents,
              ssoProvisioning: { ...cur, _pollId: null },
            },
          };
        }),
    },
    batchesPolling: {
      // --- STATE ---
      /** Indicates if there are active batches being polled */
      isPolling: false,
      /** Polling interval (ms) for checking batches status */
      pollIntervalMs: 10000, // 10 seconds in milliseconds
      /** Polling interval ID (setInterval) for batches */
      _pollId: null,
      /** Count of active batches being tracked */
      activeBatchesCount: 0,

      // --- ACTIONS ---

      /**
       * Starts polling for active batches status.
       * Polls using the provided check function at the configured interval.
       * Automatically stops polling when no active batches remain.
       * @param check - Async function to check active batches and return count
       * @param onUpdate - Optional callback when batches are updated
       * @param onComplete - Optional callback when all batches are completed
       */
      startPolling: (
        check: () => Promise<number>,
        onUpdate?: (count: number) => void,
        onComplete?: () => void,
      ) =>
        set((s) => {
          const cur = s.appEvents.batchesPolling;

          // Stop any previous polling interval to avoid duplicates
          if (cur._pollId) clearInterval(cur._pollId);

          /**
           * Polling tick function.
           * Checks for active batches and invokes callbacks.
           */
          const doTick = async () => {
            try {
              const activeCount = await check();

              const snap = get().appEvents.batchesPolling;

              // Update the count
              set((s2) => ({
                appEvents: {
                  ...s2.appEvents,
                  batchesPolling: {
                    ...s2.appEvents.batchesPolling,
                    activeBatchesCount: activeCount,
                  },
                },
              }));

              // Call update callback
              onUpdate?.(activeCount);

              // If no active batches, stop polling
              if (activeCount === 0) {
                if (snap._pollId) clearInterval(snap._pollId);
                set((s2) => ({
                  appEvents: {
                    ...s2.appEvents,
                    batchesPolling: {
                      ...s2.appEvents.batchesPolling,
                      isPolling: false,
                      _pollId: null,
                    },
                  },
                }));
                onComplete?.();
              }
            } catch (error) {
              // On error, continue polling but log the error
              console.error('Error polling batches:', error);
            }
          };

          // Start polling interval for batches status
          const pollId = window.setInterval(doTick, cur.pollIntervalMs);
          // Execute the first polling tick immediately
          void doTick();

          return {
            appEvents: {
              ...s.appEvents,
              batchesPolling: {
                ...cur,
                isPolling: true,
                _pollId: pollId,
              },
            },
          };
        }),

      /**
       * Stops the batches polling interval.
       * Clears the polling interval. Does not reset activeBatchesCount —
       * count is owned by fetchActiveBatches / WS progress (polling is WS-offline fallback only).
       */
      stopPolling: () =>
        set((s) => {
          const cur = s.appEvents.batchesPolling;
          if (cur._pollId) clearInterval(cur._pollId);
          return {
            appEvents: {
              ...s.appEvents,
              batchesPolling: {
                ...cur,
                isPolling: false,
                _pollId: null,
              },
            },
          };
        }),

      /**
       * Manually updates the active batches count.
       * @param count - The new count of active batches
       */
      setActiveBatchesCount: (count: number) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            batchesPolling: {
              ...s.appEvents.batchesPolling,
              activeBatchesCount: count,
            },
          },
        })),
    },
    tooManyRequests: {
      // --- STATE ---
      /** Epoch (ms) when the lockout window ends; null when no lockout is active */
      lockedUntil: null,
      /** Live countdown in seconds, derived from lockedUntil */
      secondsLeft: 0,
      /** Internal interval id for the 1s tick (runtime only) */
      _tickId: null,

      // --- ACTIONS ---

      /**
       * Opens / re-arms the lockout for `durationMs` (default 15000).
       * Resets the countdown on every call so subsequent 429s extend the lockout.
       * Schedules a 1s tick that auto-closes the overlay when the window ends.
       */
      open: (durationMs = 15000) =>
        set((s) => {
          const cur = s.appEvents.tooManyRequests;
          // Clear any previous tick interval so we don't accumulate duplicates
          if (cur._tickId) clearInterval(cur._tickId);

          const lockedUntil = Date.now() + Math.max(0, durationMs);
          const secondsLeft = Math.max(0, Math.ceil(durationMs / 1000));

          // Re-arm 1s tick to keep secondsLeft in sync with the wall clock
          const tickId = window.setInterval(() => {
            get().appEvents.tooManyRequests.tick();
          }, 1000);

          return {
            appEvents: {
              ...s.appEvents,
              tooManyRequests: {
                ...cur,
                lockedUntil,
                secondsLeft,
                _tickId: tickId,
              },
            },
          };
        }),

      /**
       * Closes the overlay and clears any active tick interval.
       */
      close: () =>
        set((s) => {
          const cur = s.appEvents.tooManyRequests;
          if (cur._tickId) clearInterval(cur._tickId);
          return {
            appEvents: {
              ...s.appEvents,
              tooManyRequests: {
                ...cur,
                lockedUntil: null,
                secondsLeft: 0,
                _tickId: null,
              },
            },
          };
        }),

      /**
       * Recomputes secondsLeft from lockedUntil; closes the overlay when the window has expired.
       */
      tick: () => {
        const cur = get().appEvents.tooManyRequests;
        if (cur.lockedUntil == null) return;

        const remainingMs = cur.lockedUntil - Date.now();
        if (remainingMs <= 0) {
          get().appEvents.tooManyRequests.close();
          return;
        }

        set((s) => ({
          appEvents: {
            ...s.appEvents,
            tooManyRequests: {
              ...s.appEvents.tooManyRequests,
              secondsLeft: Math.max(0, Math.ceil(remainingMs / 1000)),
            },
          },
        }));
      },

      /**
       * Re-arms the countdown after store rehydration when `lockedUntil` is still in the future.
       * If the window has already expired (or no lock is set) state is reset via `close()`.
       */
      restoreFromPersistence: () => {
        const cur = get().appEvents.tooManyRequests;
        if (cur.lockedUntil == null || cur.lockedUntil <= Date.now()) {
          get().appEvents.tooManyRequests.close();
          return;
        }

        // Clear any leftover interval before arming a fresh one
        if (cur._tickId) clearInterval(cur._tickId);

        const remainingMs = cur.lockedUntil - Date.now();
        const tickId = window.setInterval(() => {
          get().appEvents.tooManyRequests.tick();
        }, 1000);

        set((s) => ({
          appEvents: {
            ...s.appEvents,
            tooManyRequests: {
              ...s.appEvents.tooManyRequests,
              secondsLeft: Math.max(0, Math.ceil(remainingMs / 1000)),
              _tickId: tickId,
            },
          },
        }));
      },
    },
    settingsScroll: {
      // --- STATE ---
      /** Last clicked setting item key for scroll restoration */
      lastClickedSettingKey: null,

      // --- ACTIONS ---

      /**
       * Sets the last clicked setting key for scroll restoration.
       * @param key - The setting type key that was clicked
       */
      setLastClickedSetting: (key: string | null) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            settingsScroll: {
              ...s.appEvents.settingsScroll,
              lastClickedSettingKey: key,
            },
          },
        })),

      /**
       * Clears the saved setting key, resetting it to null.
       * Used when navigating from sidebar or when scroll should not be restored.
       */
      clearLastClickedSetting: () =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            settingsScroll: {
              ...s.appEvents.settingsScroll,
              lastClickedSettingKey: null,
            },
          },
        })),
    },
    fileShare: {
      /** Last permission chosen when sharing with new users/groups (`true` = viewer). Persisted. */
      isNewFilesShareRecipientsReadOnly: true,
      setIsNewFilesShareRecipientsReadOnly: (readOnly: boolean) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            fileShare: {
              ...s.appEvents.fileShare,
              isNewFilesShareRecipientsReadOnly: readOnly,
            },
          },
        })),
    },
    tagsList: {
      /** Whether the sidebar tags list shows all tags past the max-visible ceiling. Persisted. */
      isExpanded: false,
      setIsExpanded: (expanded: boolean) =>
        set((s) => ({
          appEvents: {
            ...s.appEvents,
            tagsList: {
              ...s.appEvents.tagsList,
              isExpanded: expanded,
            },
          },
        })),
    },
    fileCreation: {
      createFolderToken: 0,
      createFileFromTemplate: {
        sequence: 0,
        template: null,
      },
      createSecretFile: {
        sequence: 0,
        preset: null,
      },
      createNoteFileToken: 0,
      triggerCreateFolder: () =>
        set((state) => {
          const current = state.appEvents.fileCreation;
          return {
            appEvents: {
              ...state.appEvents,
              fileCreation: {
                ...current,
                createFolderToken: current.createFolderToken + 1,
              },
            },
          };
        }),
      triggerCreateFileFromTemplate: (template: FileTemplateType) =>
        set((state) => {
          const current = state.appEvents.fileCreation;
          return {
            appEvents: {
              ...state.appEvents,
              fileCreation: {
                ...current,
                createFileFromTemplate: {
                  sequence: current.createFileFromTemplate.sequence + 1,
                  template,
                },
              },
            },
          };
        }),
      triggerCreateSecretFile: (preset = 'credentials' as const) =>
        set((state) => {
          const current = state.appEvents.fileCreation;
          return {
            appEvents: {
              ...state.appEvents,
              fileCreation: {
                ...current,
                createSecretFile: {
                  sequence: current.createSecretFile.sequence + 1,
                  preset,
                },
              },
            },
          };
        }),
      triggerCreateNoteFile: () =>
        set((state) => {
          const current = state.appEvents.fileCreation;
          return {
            appEvents: {
              ...state.appEvents,
              fileCreation: {
                ...current,
                createNoteFileToken: current.createNoteFileToken + 1,
              },
            },
          };
        }),
    } as FileCreationEventsState & FileCreationEventsActions,
    keyboardShortcuts: {
      openCreateMenuToken: 0,
      uploadFileToken: 0,
      uploadFolderToken: 0,
      fileMenuActionEvent: {
        action: null,
        sequence: 0,
      },
      openCreateMenu: () =>
        set((state) => ({
          appEvents: {
            ...state.appEvents,
            keyboardShortcuts: {
              ...state.appEvents.keyboardShortcuts,
              openCreateMenuToken: state.appEvents.keyboardShortcuts.openCreateMenuToken + 1,
            },
          },
        })),
      triggerUploadFile: () =>
        set((state) => ({
          appEvents: {
            ...state.appEvents,
            keyboardShortcuts: {
              ...state.appEvents.keyboardShortcuts,
              uploadFileToken: state.appEvents.keyboardShortcuts.uploadFileToken + 1,
            },
          },
        })),
      triggerUploadFolder: () =>
        set((state) => ({
          appEvents: {
            ...state.appEvents,
            keyboardShortcuts: {
              ...state.appEvents.keyboardShortcuts,
              uploadFolderToken: state.appEvents.keyboardShortcuts.uploadFolderToken + 1,
            },
          },
        })),
      triggerFileMenuAction: (action: FileMenuActionEnum) =>
        set((state) => {
          const current = state.appEvents.keyboardShortcuts.fileMenuActionEvent;
          return {
            appEvents: {
              ...state.appEvents,
              keyboardShortcuts: {
                ...state.appEvents.keyboardShortcuts,
                fileMenuActionEvent: {
                  action,
                  sequence: current.sequence + 1,
                },
              },
            },
          };
        }),
    } as KeyboardShortcutsEventsState & KeyboardShortcutsEventsActions,
  },
  isGlobalLoaderVisible: false,
  setIsGlobalLoaderVisible: (v: boolean) =>
    set((s) => ({
      ...s,
      isGlobalLoaderVisible: v,
    })),
  isUploadDisabledInCurrentFolder: false,
  setIsUploadDisabledInCurrentFolder: (v: boolean) =>
    set((s) => ({
      ...s,
      isUploadDisabledInCurrentFolder: v,
    })),
  isGlobalFilesDropzoneDisabled: false,
  setIsGlobalFilesDropzoneDisabled: (v: boolean) =>
    set((s) => ({
      ...s,
      isGlobalFilesDropzoneDisabled: v,
    })),
  aiFileExtensions: {},
  setAiFileExtensions: (v: GetFileExtensionsByCategoryResponse) =>
    set((s) => ({
      ...s,
      aiFileExtensions: v,
    })),
  allFileExtensions: [],
  setAllFileExtensions: (v: GetAllFileExtensionsResponse) =>
    set((s) => ({
      ...s,
      allFileExtensions: v,
    })),
  tags: [],
  setTags: (v: Tag[]) =>
    set((s) => ({
      ...s,
      tags: v,
    })),
  remotegrantApplications: [],
  setRemotegrantApplications: (v: RemotegrantApplication[]) =>
    set((s) => ({
      ...s,
      remotegrantApplications: v,
    })),
  /**
   * Reset 2FA promo overlay session state on logout so remindAt / neverShowAgain
   * do not leak to the next logged-in user (persisted in localStorage).
   */
  resetActivateTotpOnLogout: () =>
    set((s) => ({
      appEvents: {
        ...s.appEvents,
        activateTotp: {
          ...s.appEvents.activateTotp,
          isOpen: false,
          isMandatory: false,
          neverShowAgain: false,
          remindAt: null,
          remindDays: 7,
        },
      },
    })),
});
